import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { PlatformId } from '../config/menuRegistry';

interface MenuPermissionsContextType {
  // null = acesso total; array = apenas as chaves listadas
  allowedMenus: string[] | null;
  loading: boolean;
  primaryPlatform: PlatformId | null;
  canSee: (key: string) => boolean;
  canSeePlatform: (platform: PlatformId | string) => boolean;
  refresh: () => Promise<void>;
}

const MenuPermissionsContext = createContext<MenuPermissionsContextType>({
  allowedMenus: null,
  loading: false,
  primaryPlatform: 'crm',
  canSee: () => true,
  canSeePlatform: () => true,
  refresh: async () => {}
});

export function MenuPermissionsProvider({ children }: { children: ReactNode }) {
  const { user, token, isAuthenticated } = useAuth();
  const [allowedMenus, setAllowedMenus] = useState<string[] | null>(null);
  const [primaryPlatform, setPrimaryPlatform] = useState<PlatformId | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchPermissions = async () => {
    if (!isAuthenticated || !token) {
      setAllowedMenus(null);
      setPrimaryPlatform(null);
      return;
    }
    setLoading(true);
    try {
      const response = await fetch('/api/menu-permissions/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllowedMenus(Array.isArray(data.menuPermissions) ? data.menuPermissions : null);
        setPrimaryPlatform(data.primaryPlatform || 'crm');
      } else {
        setAllowedMenus(null);
        setPrimaryPlatform('crm');
      }
    } catch {
      // Em caso de falha, não bloqueia o uso do sistema
      setAllowedMenus(null);
      setPrimaryPlatform('crm');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
    // Recarrega quando troca de usuário/tenant
  }, [isAuthenticated, token, user?.id, user?.tenantId]);

  const canSee = (key: string) => allowedMenus === null || allowedMenus.includes(key);
  const canSeePlatform = (platform: PlatformId | string) =>
    allowedMenus === null || allowedMenus.some(k => k.startsWith(`${platform}.`));

  return (
    <MenuPermissionsContext.Provider value={{ allowedMenus, loading, primaryPlatform, canSee, canSeePlatform, refresh: fetchPermissions }}>
      {children}
    </MenuPermissionsContext.Provider>
  );
}

export function useMenuPermissions() {
  return useContext(MenuPermissionsContext);
}
